// alterView — DdlEdit builder + commit dispatcher for the engine-correct
// "in-place ALTER VIEW" idiom. DSE2 has no dedicated `alter-view` op — the
// canonical update path is `create-view` with `mssqlOrAlter: true` (mssql)
// or `orReplace: true` (pgsql/mysql/oracle). sqlite has no atomic ALTER and
// is unsupported — the consumer surfaces a documented redirect tooltip.
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D2 — View / Materialized View UI.

import type { SqlEngine, QualifiedName } from '../../../../sql-grammar/model';
import type { DdlEdit, ViewDef } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';
import { applyAlterFlagForEngine } from './createView';

export interface AlterViewArgs {
  /** Existing view's qualified name. */
  readonly viewName: QualifiedName;
  /** New SELECT body. */
  readonly bodyText: string;
  /** Optional engine-specific options carried over from the view's definition
   *  (e.g. mssql SCHEMABINDING, pgsql WITH options). The synthesizer respects
   *  these on the OR ALTER / OR REPLACE emit. */
  readonly viewOptions?: Omit<ViewDef, 'name' | 'bodyText' | 'orReplace' | 'mssqlOrAlter'>;
}

export function buildAlterViewEdit(
  engine: SqlEngine,
  args: AlterViewArgs,
): DdlEdit {
  const view: ViewDef = applyAlterFlagForEngine(engine, {
    name: args.viewName,
    bodyText: args.bodyText,
    ...(args.viewOptions ?? {}),
  });
  return { op: 'create-view', view };
}

export async function commitAlterView(
  ctx: VqeDdlEditContext,
  args: AlterViewArgs,
): Promise<void> {
  const edit = buildAlterViewEdit(ctx.service.engineId, args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const ALTER_VIEW_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mssql', 'postgres', 'mysql', 'oracle',
] as const);

export function isAlterViewSupported(engine: SqlEngine): boolean {
  return ALTER_VIEW_SUPPORTED.has(engine);
}

export function getAlterViewUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return (
      'SQLite has no atomic ALTER / CREATE OR REPLACE VIEW. Drop and recreate '
      + 'the view in two steps (DROP VIEW IF EXISTS <name>; CREATE VIEW <name> AS …).'
    );
  }
  return '';
}

/** Pre-flight validator. Returns `null` when args are acceptable, else a
 *  user-facing message. */
export function validateAlterViewForEngine(
  engine: SqlEngine,
  args: AlterViewArgs,
): string | null {
  if (!isAlterViewSupported(engine)) {
    return getAlterViewUnsupportedTooltip(engine);
  }
  if (args.bodyText.trim().length === 0) {
    return 'View body (SELECT) is required.';
  }
  if (args.viewName.name.trim().length === 0) {
    return 'View name is required.';
  }
  return null;
}
